import {useCallback, useEffect, useState} from 'react';

import {useAbortSignal} from '@pb/kit-hooks-use-abort-signal';

import type {Error} from 'src/openapi/admin/models/Error';
import type {ReadOnlyHookReturn} from 'src/typedef';

type Props<Data, > = {
    initValue: Data,
    getDispatchAction: (signal: AbortSignal) => Promise<{
        type: string,
        payload: {
            data: Data,
            success: true,
            error: false,
        } | {
            data: Error,
            success: false,
            error: true,
        },
    }>,
    shouldLoadData?: boolean,
};

export const useReadOnlyDataHook = <Data, >(params: Props<Data>): ReadOnlyHookReturn<Data> => {
    const {getDispatchAction, shouldLoadData = true, initValue} = params;
    const {createAbortSignal} = useAbortSignal();

    const [data, setData] = useState<Data>(initValue as Data);
    const [loadingError, setLoadingError] = useState<Error | null>(null);
    const [isLoading, setIsLoading] = useState(shouldLoadData);

    const loadData = useCallback(async () => {
        setIsLoading(true);
        const res = await getDispatchAction(createAbortSignal());
        setIsLoading(false);

        if (res.payload.success) {
            setData(res.payload.data);
            setLoadingError(null);
        } else if (res.payload.error) {
            setLoadingError(res.payload.data);
        }
    }, [getDispatchAction, createAbortSignal, setLoadingError, setData, setIsLoading]);

    useEffect(() => {
        if (shouldLoadData) {
            loadData();
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return {
        isLoading,
        data,
        loadData,
        loadingError,
        createAbortSignal,
    };
};
